import { useEffect, useState } from 'react'
import { MdSearch } from 'react-icons/md'
import toastService from '../../services/toastService'
import usersAndAccountsService from '../../services/usersAndAccountsService'
import CustomPagination from '../CustomPagination/CustomPagination'
import Loader from '../Loader/Loader'

import { useNavigate } from 'react-router-dom'
import styles from './TableOrganizations.module.scss'
import organizationService from '../../services/organizationService'
import { exportToXLSX } from '../../utils/exportToXLSX'
import dayjs from 'dayjs'

const itemsPerPage = 10
const siblingCount = 1
const PAGING_SELECT_OPTIONS = ['10', '20', '50', '100']

const PLAN_STATUS_OPTIONS = [
  { value: '', label: 'All organizations' },
  { value: 'active', label: 'Active subscription' },
  { value: 'trial', label: 'Free trial' },
  { value: 'former', label: 'Former customers (no plan)' },
  { value: 'never', label: 'Never subscribed' },
]

const PLAN_STATUS_LABELS = {
  active: 'Active',
  trial: 'Trial',
  former: 'Former',
  never: 'Never subscribed',
  group: 'Group',
}

const planStatusLabel = (status) => PLAN_STATUS_LABELS[status] || status || ''

const numbersLabel = (organization) => {
  if (organization.twilioNumber) {
    return organization.numbersReleaseAt
      ? `${organization.twilioNumber} (release on ${dayjs(organization.numbersReleaseAt).format(
          'MMM D, YYYY',
        )})`
      : organization.twilioNumber
  }
  return organization.planStatus === 'former' ? 'Released' : ''
}

const TableOrganizations = () => {
  const [organizations, setOrganizations] = useState([])
  const [totalCount, setTotalCount] = useState(1)
  const [currentPage, setCurrentPage] = useState(1)
  const [limit, setLimit] = useState(itemsPerPage)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)

  const navigate = useNavigate()

  const getOrganizations = () => {
    setLoading(true)
    usersAndAccountsService
      .getOrganizations({
        page: currentPage - 1,
        limit: limit,
        search: search,
        status: status,
      })
      .then((response) => {
        setOrganizations(response?.organizations || [])
        setTotalCount(response?.total || 0)
        const totalPageCount = Math.ceil((response?.total || 0) / limit)
        if (currentPage > totalPageCount) setCurrentPage(1)
      })
      .catch((err) => {
        toastService.show('error', 'Error fetch organizations' + err?.message || '')
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    getOrganizations()
  }, [currentPage, limit, status])

  const handlePageSizeChagne = (size) => {
    setLimit(size)
    setCurrentPage(1)
  }

  const handleStatusChange = (e) => {
    setStatus(e.target.value)
    setCurrentPage(1)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.type === 'click') {
      getOrganizations()
    }
  }

  // Exports every organization of the selected segment, e.g. for re-engagement campaigns
  const handleExport = async () => {
    setExporting(true)
    try {
      const response = await usersAndAccountsService.getOrganizations({
        page: 0,
        limit: totalCount || 1,
        search: search,
        status: status,
      })
      const rows = (response?.organizations || [])
        .filter((organization) => organization.active === 1)
        .map((organization) => ({
          Organization: organization.name,
          Username: organization.username,
          Email: organization.email || '',
          'Plan status': planStatusLabel(organization.planStatus),
          Plan: organization.subscription?.planName || '',
          'VoIP number': numbersLabel(organization),
        }))
      exportToXLSX(rows, `organizations-${status || 'all'}`)
    } catch (err) {
      toastService.show('error', 'Error exporting organizations' + err?.message || '')
    } finally {
      setExporting(false)
    }
  }

  const onNavigateHandler = (organization) => {
    if (organization?.parentId === null) {
      organizationService.getMainOrganizationForMasterAdmin(organization._id)
      navigate(`/dashboard/mainOrganizations/${organization._id}/main`)
    } else {
      navigate(`/dashboard/organization/${organization.parentId}/list`)
    }
  }

  if (loading) {
    return (
      <div>
        <Loader />
      </div>
    )
  }

  return (
    <div>
      <div className={styles.info}>
        <div>
          <h3>ORGANIZATIONS</h3>
          <span>total: {totalCount}</span>
        </div>
        <div className={styles.filters}>
          <select
            className='form-select bordered'
            value={status}
            onChange={handleStatusChange}
            aria-label='Filter by plan status'
          >
            {PLAN_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            className='btn btn-lightblue'
            onClick={handleExport}
            disabled={exporting || !totalCount}
          >
            {exporting ? 'Exporting…' : 'Export'}
          </button>
        </div>
        <div className={styles.inputWrapper}>
          <input
            placeholder='Search by organization name or username or twilio number'
            className='form-control bordered'
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            value={search}
          />
          <MdSearch className={styles.searchIcon} onClick={handleKeyDown} />
        </div>
      </div>
      <div className={`${'p-0 pb-3 table-responsive card-body '}`}>
        <table className={`${'table mb-0'}`}>
          <thead className='lightblue-bg'>
            <tr>
              <th>Organizations Name</th>
              <th>Username</th>
              <th>Plan status</th>
              <th>Twilio Number</th>
            </tr>
          </thead>
          <tbody>
            {organizations?.map((organization, index) =>
              organization.active === 1 ? (
                <tr
                  onClick={() => {
                    onNavigateHandler(organization)
                  }}
                  key={index}
                >
                  <td>{organization.name}</td>
                  <td>{organization.username}</td>
                  <td>
                    <span
                      className={`${styles.planStatus} ${styles[organization.planStatus] || ''}`}
                    >
                      {planStatusLabel(organization.planStatus)}
                    </span>
                  </td>
                  <td>{numbersLabel(organization)}</td>
                </tr>
              ) : null,
            )}
          </tbody>
        </table>
      </div>
      <div className={styles.paginationWrapper}>
        <CustomPagination
          totalCount={totalCount}
          currentPage={currentPage}
          pageSize={limit}
          siblingCount={siblingCount}
          onPageChage={setCurrentPage}
          options={PAGING_SELECT_OPTIONS}
          hasSelect={true}
          handleSelectChange={handlePageSizeChagne}
        />
      </div>
    </div>
  )
}

export default TableOrganizations
